import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { useMessage } from '@tmapy/intl';
import { useDispatch } from '@tmapy/redux';

import './ApprovalItem.scss';

import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Dialog,
  Link as StyleGuideLink,
  SecondaryBtn,
} from '@tmapy/style-guide';

import type { OrderApprovalNestedResponse } from '../../openapi/types/OrderApprovalNested';
import { useApproverRolesListResource } from '../../openapi/operations/approverRolesList';
import { ordersApprovePartialUpdate } from '../../openapi/operations/ordersApprovePartialUpdate';
import type { OrderRetrieveResponse } from '../../openapi/types';

import type { UseResource } from '../../hooks/useResource';
import type { ServerErrors } from '../../types';
import { useAuth, useFormItemServerErrors } from '../../hooks';
import { Link } from '../../link';
import { QUERY_STATE } from '../../constants';
import { User } from '../../users/components/User';
import { BoxItem } from '../../app/components/BoxItem';

import { ORDER_STATE, APPROVAL_STATE } from '../constants';

import { orderApprovalMessages as msg, orderApprovalStateMessages } from './orderMessages';
import { RejectionDialog } from './RejectionDialog';
import { ApprovalDialog } from './ApprovalDialog';

type ApprovalItemProps = OrderApprovalNestedResponse & {
  orderId: number;
  orderState: ORDER_STATE;
  rejection_reason?: string | null;
  resource: UseResource<OrderRetrieveResponse>;
};

export const ApprovalItem: React.FC<ApprovalItemProps> = (props) => {
  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const auth = useAuth();
  const [isRejectionDialogVisible, setRejectionDialogVisibility] = useState(false);
  const [isApprovalDialogVisible, setApprovalDialogVisibility] = useState(false);
  const [isUserDetailDialogVisible, setUserDetailDialogVisibility] = useState(false);
  const [queryApprovedState, setQueryApprovedState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const canListApproverRoles = auth.hasPermission('ApproverRoles', 'list', null);
  const approverRolesList = useApproverRolesListResource(canListApproverRoles ? {} : null, true);
  const approverRole = approverRolesList.data?.results?.find((role) => role.name === props.role);

  const approverDetailProps = { id: props.approver?.id };

  const handleApprovalFormConfirm = useCallback(
    async (approval_reason: string) => {
      setApprovalDialogVisibility(false);
      setQueryApprovedState(QUERY_STATE.WAITING);
      setServerErrors(undefined);

      const respond = await ordersApprovePartialUpdate({
        query: { id: props.orderId },
        request: {
          approval_id: props.id,
          state: APPROVAL_STATE.APPROVED,
          approval_reason,
        },
      });

      if (!respond.error && respond.ok) {
        setQueryApprovedState(QUERY_STATE.SUCCESS);
        props.resource.refetch();
      } else {
        setServerErrors(respond.error?.data);
        setQueryApprovedState(QUERY_STATE.FAIL);
      }
    },
    [props.orderId, props.id, dispatch, props.resource],
  );

  const handleRejectionFormConfirm = useCallback(
    async (rejection_reason: string) => {
      setRejectionDialogVisibility(false);
      setQueryApprovedState(QUERY_STATE.WAITING);
      setServerErrors(undefined);

      const respond = await ordersApprovePartialUpdate({
        query: { id: props.orderId },
        request: {
          approval_id: props.id,
          state: APPROVAL_STATE.REJECTED,
          rejection_reason,
        },
      });
      if (!respond.error && respond.ok) {
        setQueryApprovedState(QUERY_STATE.SUCCESS);
        props.resource.refetch();
      } else {
        setServerErrors(respond.error?.data);
        setQueryApprovedState(QUERY_STATE.FAIL);
      }
    },
    [props.orderId, props.id, dispatch, props.resource],
  );

  const handleRejectBtnClick = () => {
    setRejectionDialogVisibility(true);
  };

  const handleApproveBtnClick = () => {
    setApprovalDialogVisibility(true);
  };

  const handleUserDetailBtnClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setUserDetailDialogVisibility(true);
  }, []);

  const handleDialogClose = () => {
    setRejectionDialogVisibility(false);
    setApprovalDialogVisibility(false);
    setUserDetailDialogVisibility(false);
  };

  return (
    <BoxItem
      stateColorClassName={classNames({
        'dmx-approvalItem-approved': props.state === APPROVAL_STATE.APPROVED,
        'dmx-approvalItem-waiting': props.state === APPROVAL_STATE.WAITING,
        'dmx-approvalItem-rejected': props.state === APPROVAL_STATE.REJECTED,
      })}
      stateTooltip={formatMessage.lookup(orderApprovalStateMessages, props.state) ?? props.state}
    >
      <div className='sg-a-pb-1'>
        <div className='sg-a-d-f sg-a-ai-c sg-a-jc-sb'>
          <div>
            <header>
              <span className='sg-u-type-bold'>{props.label}</span>
              {approverRole?.description && (
                <>
                  {' '}
                  <span className='sg-u-type-xsm sg-u-c-body-darker'>
                    {approverRole?.description}
                  </span>
                </>
              )}
            </header>
            {props.state !== APPROVAL_STATE.WAITING && (
              <p className='sg-u-type-sml sg-a-mt-1/2'>
                {props.approved && (
                  <b className='sg-u-type-bold sg-u-type-italic'>
                    {new Date(props.approved).toLocaleString('cs-CZ', {
                      day: 'numeric',
                      year: 'numeric',
                      month: 'long',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </b>
                )}
                <FormattedMessage {...msg.orderApprovalResult} values={{ state: props.state }} />
                <span className='sg-u-type-italic'>
                  <b className='sg-u-type-bold'>
                    <Link
                      to='vydejUserDetail'
                      params={approverDetailProps}
                      onClick={handleUserDetailBtnClick}
                      severity='colorless'
                    >
                      {props.approver.name} {props.approver.surname}
                    </Link>
                  </b>
                </span>
                {props.approver.email && (
                  <>
                    {', '}
                    <StyleGuideLink href={`mailto:${props.approver.email}`} severity='colorful'>
                      {props.approver.email}
                    </StyleGuideLink>
                  </>
                )}
                {props.approved && '.'}
                {props.state === APPROVAL_STATE.APPROVED && props.approval_reason && (
                  <>
                    {' '}
                    <FormattedMessage {...msg.orderApprovalReason} />
                  </>
                )}
                {props.state === APPROVAL_STATE.REJECTED && props.rejection_reason && (
                  <>
                    {' '}
                    <FormattedMessage {...msg.orderRejectionReason} />
                  </>
                )}
              </p>
            )}
          </div>

          {props.can_approve &&
            props.orderState === ORDER_STATE.WAITING_FOR_APPROVAL &&
            queryApprovedState !== QUERY_STATE.WAITING &&
            props.state === APPROVAL_STATE.WAITING && (
              <footer className='sg-a-f-n sg-a-ml-2'>
                <BtnBar>
                  <CtaBtn size="sml" onClick={handleApproveBtnClick}>
                    <FormattedMessage {...msg.orderApproveBtn} />
                  </CtaBtn>
                  <SecondaryBtn isDanger size="sml" onClick={handleRejectBtnClick}>
                    <FormattedMessage {...msg.orderRejectBtn} />
                  </SecondaryBtn>
                </BtnBar>
              </footer>
            )}
        </div>

        {props.state === APPROVAL_STATE.APPROVED && props.approval_reason && (
          <p className='sg-a-mt-1'>
            <q className='dmx-approvalItem--reason'>{props.approval_reason}</q>
          </p>
        )}
        {props.state === APPROVAL_STATE.REJECTED && props.rejection_reason && (
          <p className='sg-a-mt-1'>
            <q className='dmx-approvalItem--reason'>{props.rejection_reason}</q>
          </p>
        )}

        {queryApprovedState === QUERY_STATE.FAIL && (
          <div className='sg-a-mt-1'>
            <DangerAlert>
              {formItemServerErrors.non_field_errors?.errorMessage || (
                <FormattedMessage {...msg.orderApprovalFail} />
              )}
            </DangerAlert>
          </div>
        )}

        {isRejectionDialogVisible && (
          <Dialog width='400px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
            <RejectionDialog
              confirmRejected={handleRejectionFormConfirm}
              closeRejectionDialog={handleDialogClose}
            />
          </Dialog>
        )}
        {isApprovalDialogVisible && (
          <Dialog width='400px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
            <ApprovalDialog
              confirmApproved={handleApprovalFormConfirm}
              closeApprovalDialog={handleDialogClose}
            />
          </Dialog>
        )}

        {isUserDetailDialogVisible && (
          <Dialog width='800px' onEscKeyDown={handleDialogClose} onOverlayClick={handleDialogClose}>
            <User userId={props.approver?.id} onCloseDialog={handleDialogClose} />
          </Dialog>
        )}
      </div>
    </BoxItem>
  );
};
